import React, { useContext, useState } from "react";
import { ReactComponent as CloseIcon } from "../../../../assets/modal-close.svg";
import { useContentAndProgress } from "../../../../contexts/UserProgressContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { ReactComponent as Rocket } from "../../../../assets/rocket.svg";
import { ReactComponent as Ellipse } from "../../../../assets/Ellipse.svg";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../../components/button/types";
import { useMilestoneNavigation } from "../../../../hooks/useMilestoneNavigation";
import UnlockPhaseModal from "../content/components/UnlockPhaseModal";

interface PhaseChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PhaseChangeModal: React.FC<PhaseChangeModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { activePhase, phases } = useContentAndProgress();
  const { user, configurations } = useContext(AuthContext);

  const productType = configurations?.productType;
  const isBLUE = productType === "BLUE";
  const journeyId = user?.userDetails.journeyId;
  const { t } = useTranslation("home");
  const { jumpToNextPhase } = useMilestoneNavigation();
  const { t: tunlock } = useTranslation("unlock");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hideModal, setHideModal] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setHideModal(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setHideModal(false);
  };

  if (!phases) {
    return null;
  }
  const currentPhaseIndex = phases.findIndex(
    (phase) => phase.id === activePhase?.id
  );

  // Get the next phase if it exists
  const nextPhase =
    currentPhaseIndex !== -1 && currentPhaseIndex + 1 < phases.length
      ? phases[currentPhaseIndex + 1]
      : null;

  const lastPhase = phases?.[phases.length - 1];

  const isLastPhase = lastPhase === nextPhase;

  const hasJobType = user?.userDetails?.newJobType;
  const isRestartPhase = lastPhase?.content?.restartPhase;

  const handleConfirmNavigation = () => {
    onClose();
    jumpToNextPhase(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={` ${
          hideModal ? "hidden" : ""
        } fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center  `}
      >
        <div className="w-full h-full bg-[#FFDA6F] relative overflow-y-auto px-[24px] lg:w-1/3 xl:w-1/4 lg:h-auto lg:p-4 lg:rounded-md">
          {/* X button to close the modal */}
          <div
            className="flex justify-end items-center gap-2"
            style={{ height: "64px", padding: "" }}
          >
            <button
              className="cursor-pointer"
              onClick={onClose} // Close the modal
            >
              <CloseIcon />
            </button>
          </div>

          <div>
            <div className="flex items-center justify-center w-full font-[600] text-[12px] leading-3 text-center">
              {`Phase ${
                (activePhase?.content?.orderForProgress[journeyId ?? ""] || 0) +
                1
              }`}
            </div>
            <div className="flex items-center justify-center w-full font-[700] text-[32px] leading-9 text-center">
              {activePhase?.title}
            </div>
          </div>
          <div className="flex items-center justify-center w-full mt-[24px] relative">
            <svg
              width="150"
              height="150"
              viewBox="0 0 156 156"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="rotate(48 78 78)">
                <circle
                  cx="78"
                  cy="78"
                  r="75"
                  stroke="white"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="20 20"
                />
              </g>
            </svg>
            {/* Center Rocket */}
            <Rocket className="absolute inset-0 m-auto w-1/2 h-1/2" />{" "}
            {/* Adjust size as needed */}
            <div className="absolute translate-y-[170%] left-1/2 transform -translate-x-1/2 bg-[#FFDA6F] px-6 rounded text-center font-[700] text-[28px] leading-9">
              {" "}
              {Math.round(activePhase?.progressValue || 0)}%
            </div>
          </div>
          <div className="px-6 h-[1px] bg-white mt-[24px] mb-[24px]"></div>

          <div className="flex items-center justify-center w-full font-[400] text-[14px] mb-[14px] leading-4 text-center">
            {t("between-phases-modal-text")}:
          </div>

          <div className="bg-white p-6 rounded-lg mb-2 min-w-full ">
            <div className="flex items-center justify-center w-full font-[700] text-[16px] leading-5 text-center">
              {`Phase ${
                (activePhase?.content?.orderForProgress[journeyId ?? ""] || 0) +
                2
              }`}
            </div>
            <div className="flex items-center justify-center w-full font-[700] text-[24px] leading-7 mb-[16px] text-center">
              {nextPhase?.title}
            </div>
            <div className="flex w-full font-[500] text-[14px] leading-4 ">
              {nextPhase?.description}
            </div>

            {isLastPhase && !hasJobType && isRestartPhase ? (
              <>
                <div
                  className=" mt-6 bg-Blue-ligth rounded-lg cursor-pointer p-3"
                  onClick={handleOpenModal}
                >
                  <div className=" text-[#757575]">
                    {tunlock("mobileListText")}
                  </div>
                  <div className=" flex justify-end font-semibold">
                    {tunlock("mobileListButton1")}{" "}
                    {(nextPhase?.content?.orderForProgress[journeyId ?? ""] ||
                      0) + 1}{" "}
                    {tunlock("mobileListButton2")}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mt-[36px]">
                  <Button
                    className="w-1/2 mr-1"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.MEDIUM}
                    onClick={onClose}
                  >
                    {t("back")}
                  </Button>
                  <Button
                    className="w-1/2 ml-[24px] border border-black"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.MEDIUM}
                    onClick={handleConfirmNavigation}
                  >
                    {t("forward")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <UnlockPhaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        activePhase={activePhase}
      />
    </>
  );
};

export default PhaseChangeModal;
